/**
 * FormatSpecEditor — type-aware format controls for a single ColumnFormatSpec.
 *
 * EX-2 / F-053 / Task 12.
 *
 * Renders ONLY the controls relevant to `spec.kind`:
 *   text   → trim / case / maxLength / truncateFrom
 *   date   → token select
 *   number → decimals / thousandsSeparator / currencySymbol / negativeStyle
 *   raw    → help line, no interactive controls
 *
 * Switching `spec.kind` is the parent component's responsibility (Task 13).
 *
 * Follows the rb-* class + htmlFor label conventions used in sibling components
 * (FormatColumnsEditor, MappingRowFormatEditor).
 */

import type { ColumnFormatSpec } from '@capture/shared';

const DATE_TOKENS = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY/MM/DD', 'M/D/YYYY'] as const;

export function FormatSpecEditor({
  spec,
  onChange,
  idPrefix,
}: {
  spec: ColumnFormatSpec;
  onChange: (next: ColumnFormatSpec) => void;
  idPrefix: string;
}) {
  if (spec.kind === 'raw') {
    return <p className="rb-help">No formatting — value is exported as-is.</p>;
  }

  if (spec.kind === 'text') {
    return (
      <div className="format-spec-editor">
        <label className="rb-label">
          <input
            type="checkbox"
            checked={spec.trim ?? false}
            onChange={(e) => onChange({ ...spec, trim: e.target.checked || undefined })}
          />{' '}
          Trim whitespace
        </label>

        <label htmlFor={`${idPrefix}-case`} className="rb-label">
          Case
        </label>
        <select
          id={`${idPrefix}-case`}
          className="rb-select"
          value={spec.case ?? ''}
          onChange={(e) =>
            onChange({
              ...spec,
              case: (e.target.value || undefined) as 'upper' | 'lower' | 'title' | undefined,
            })
          }
        >
          <option value="">— leave as-is —</option>
          <option value="upper">UPPER</option>
          <option value="lower">lower</option>
          <option value="title">Title</option>
        </select>

        <label htmlFor={`${idPrefix}-maxlen`} className="rb-label">
          Max length
        </label>
        <input
          id={`${idPrefix}-maxlen`}
          type="number"
          min={1}
          className="rb-input"
          value={spec.maxLength ?? ''}
          onChange={(e) =>
            onChange({ ...spec, maxLength: e.target.value ? Number(e.target.value) : undefined })
          }
        />

        <label htmlFor={`${idPrefix}-trunc`} className="rb-label">
          Truncate from
        </label>
        <select
          id={`${idPrefix}-trunc`}
          className="rb-select"
          value={spec.truncateFrom ?? 'end'}
          onChange={(e) =>
            onChange({
              ...spec,
              truncateFrom: e.target.value === 'end' ? undefined : 'start',
            })
          }
        >
          <option value="end">End</option>
          <option value="start">Start</option>
        </select>

        <label htmlFor={`${idPrefix}-pad-to`} className="rb-label">
          Pad to width
        </label>
        <input
          id={`${idPrefix}-pad-to`}
          type="number"
          min={1}
          className="rb-input"
          value={spec.pad?.to ?? ''}
          onChange={(e) => {
            const to = e.target.value ? Number(e.target.value) : 0;
            if (to > 0) {
              onChange({
                ...spec,
                pad: { to, char: spec.pad?.char || ' ', align: spec.pad?.align ?? 'left' },
              });
            } else {
              onChange({ ...spec, pad: undefined });
            }
          }}
        />

        <label htmlFor={`${idPrefix}-pad-char`} className="rb-label">
          Pad character
        </label>
        <input
          id={`${idPrefix}-pad-char`}
          type="text"
          className="rb-input"
          value={spec.pad?.char ?? ''}
          onChange={(e) => {
            if (!spec.pad?.to) return;
            onChange({ ...spec, pad: { ...spec.pad, char: e.target.value } });
          }}
        />

        <label htmlFor={`${idPrefix}-pad-align`} className="rb-label">
          Pad align
        </label>
        <select
          id={`${idPrefix}-pad-align`}
          className="rb-select"
          value={spec.pad?.align ?? 'left'}
          onChange={(e) => {
            if (!spec.pad?.to) return;
            onChange({
              ...spec,
              pad: { ...spec.pad, align: e.target.value as 'left' | 'right' },
            });
          }}
        >
          <option value="left">Left</option>
          <option value="right">Right</option>
        </select>
      </div>
    );
  }

  if (spec.kind === 'date') {
    return (
      <div className="format-spec-editor">
        <label htmlFor={`${idPrefix}-token`} className="rb-label">
          Date format
        </label>
        <select
          id={`${idPrefix}-token`}
          className="rb-select"
          value={spec.token}
          onChange={(e) => onChange({ kind: 'date', token: e.target.value })}
        >
          {DATE_TOKENS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // kind === 'number'
  return (
    <div className="format-spec-editor">
      <label htmlFor={`${idPrefix}-dec`} className="rb-label">
        Decimals
      </label>
      <input
        id={`${idPrefix}-dec`}
        type="number"
        min={0}
        className="rb-input"
        value={spec.decimals ?? ''}
        onChange={(e) =>
          onChange({ ...spec, decimals: e.target.value ? Number(e.target.value) : undefined })
        }
      />

      <label className="rb-label">
        <input
          type="checkbox"
          checked={spec.thousandsSeparator ?? false}
          onChange={(e) =>
            onChange({ ...spec, thousandsSeparator: e.target.checked || undefined })
          }
        />{' '}
        Thousands separator
      </label>

      <label htmlFor={`${idPrefix}-sym`} className="rb-label">
        Currency symbol
      </label>
      <input
        id={`${idPrefix}-sym`}
        type="text"
        className="rb-input"
        value={spec.currencySymbol ?? ''}
        placeholder="$, €"
        onChange={(e) => onChange({ ...spec, currencySymbol: e.target.value || undefined })}
      />

      <label htmlFor={`${idPrefix}-neg`} className="rb-label">
        Negative style
      </label>
      <select
        id={`${idPrefix}-neg`}
        className="rb-select"
        value={spec.negativeStyle ?? 'minus'}
        onChange={(e) =>
          onChange({ ...spec, negativeStyle: e.target.value as 'minus' | 'parens' })
        }
      >
        <option value="minus">Minus (-1234)</option>
        <option value="parens">Parens ((1234))</option>
      </select>
    </div>
  );
}
